import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { ZAR } from "@/lib/cart";
import {
  ChartLine,
  TrendUp,
  Package,
  CurrencyCircleDollar,
  ArrowUpRight,
} from "@phosphor-icons/react";

/**
 * Admin analytics dashboard — revenue snapshots, 30-day chart, top products.
 * All data is computed server-side from paid/shipped/delivered orders.
 */
export default function AnalyticsAdmin() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/analytics/overview")
      .then((r) => setData(r.data))
      .catch(() => setData({}))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="text-sm text-white/40">Loading analytics…</div>;
  }
  if (!data || !data.chart_last_30_days) {
    return (
      <div className="border border-white/10 bg-[#0d0d0d] p-6 text-sm text-white/60">
        Couldn't load analytics. Try again in a moment.
      </div>
    );
  }

  return (
    <div className="max-w-[1100px]">
      {/* Top-level KPIs */}
      <div className="grid gap-3 grid-cols-2 md:grid-cols-4">
        <KpiCard
          testId="kpi-revenue-today"
          Icon={CurrencyCircleDollar}
          label="REVENUE · TODAY"
          value={ZAR(data.revenue_today)}
          sub={`${data.orders_today} orders`}
          color="#00FFC2"
        />
        <KpiCard
          testId="kpi-revenue-week"
          Icon={TrendUp}
          label="REVENUE · 7 DAYS"
          value={ZAR(data.revenue_week)}
          sub={`${data.orders_week} orders`}
          color="#FFC900"
        />
        <KpiCard
          testId="kpi-revenue-month"
          Icon={ChartLine}
          label="REVENUE · 30 DAYS"
          value={ZAR(data.revenue_month)}
          sub={`${data.orders_month} orders`}
          color="#FF5A1F"
        />
        <KpiCard
          testId="kpi-revenue-total"
          Icon={Package}
          label="LIFETIME REVENUE"
          value={ZAR(data.total_revenue)}
          sub={`${data.total_orders} orders total`}
          color="#8B5CF6"
        />
      </div>

      {/* Revenue chart */}
      <div className="mt-6 border border-white/10 bg-[#0d0d0d] p-4 sm:p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="font-label text-[10px] text-white/40">DAILY REVENUE</div>
            <div className="mt-1 font-display text-lg font-bold">Last 30 days</div>
          </div>
        </div>
        <RevenueChart data={data.chart_last_30_days} />
      </div>

      {/* Top products */}
      <div className="mt-6 border border-white/10 bg-[#0d0d0d]">
        <div className="border-b border-white/10 px-4 py-3">
          <div className="font-label text-[10px] text-white/40">TOP SELLERS</div>
          <div className="mt-1 font-display text-lg font-bold">By revenue (30 days)</div>
        </div>
        {data.top_products?.length === 0 ? (
          <div className="p-6 text-sm text-white/50">No sales yet — data appears here once orders start rolling in.</div>
        ) : (
          <div className="divide-y divide-white/5">
            {data.top_products.map((p, i) => (
              <div key={p.name} className="flex items-center gap-4 p-3 sm:p-4">
                <div className="font-display text-lg font-black text-[#FF5A1F] w-6 text-center">
                  {i + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-display text-sm font-bold text-white line-clamp-1">{p.name}</div>
                  <div className="text-[11px] text-white/50">{p.qty} sold</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-display text-sm font-black text-[#00FFC2]">{ZAR(p.revenue)}</div>
                  <div className="text-[10px] text-white/40">revenue</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mt-6 text-[11px] font-label text-white/40 flex items-center gap-1.5">
        <ArrowUpRight size={11} weight="bold" />
        Data refreshes on page load. Numbers reflect paid, shipped and delivered orders.
      </div>
    </div>
  );
}

function KpiCard({ Icon, label, value, sub, color, testId }) {
  return (
    <div data-testid={testId} className="border border-white/10 bg-[#0d0d0d] p-4">
      <div className="flex items-center justify-between">
        <div className="font-label text-[9px] text-white/40 tracking-wider">{label}</div>
        <Icon size={14} weight="duotone" color={color} />
      </div>
      <div
        className="mt-2 font-display text-2xl font-black tracking-tight"
        style={{ color }}
      >
        {value}
      </div>
      {sub && <div className="mt-1 text-[10px] text-white/40">{sub}</div>}
    </div>
  );
}

function RevenueChart({ data }) {
  const max = Math.max(1, ...data.map((d) => d.revenue));
  return (
    <div className="flex items-end gap-[3px] h-40 sm:h-52">
      {data.map((d) => {
        const pct = (d.revenue / max) * 100;
        const isZero = d.revenue === 0;
        return (
          <div key={d.date} className="flex-1 group relative">
            <div
              className={`w-full transition-all duration-500 ${
                isZero ? "bg-white/5" : "bg-gradient-to-t from-[#FF5A1F] to-[#FFC900]"
              }`}
              style={{ height: `${Math.max(2, pct)}%`, minHeight: 2 }}
            />
            {!isZero && (
              <div className="absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap bg-black border border-white/15 px-2 py-1 text-[10px] opacity-0 group-hover:opacity-100 transition-opacity z-10">
                <div className="text-white/60">{d.date}</div>
                <div className="text-[#00FFC2] font-bold">{ZAR(d.revenue)}</div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
